import { KeyRound } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { Button, type ButtonProps } from '../components/button';

import {
  assertPasskey,
  createPasskey,
  describePasskeyError,
  isPasskeySupported,
  type PasskeyAuthenticationOptionsJson,
  type PasskeyAuthenticationResponseJson,
  type PasskeyRegistrationOptionsJson,
  type PasskeyRegistrationResponseJson,
} from './webauthn';

/**
 * The two passkey buttons (M2.8, SECURITY.md §4.3).
 *
 * Both take the API calls as props rather than importing a client: `packages/ui` has no
 * dependency on `@platform/api-client` and must not acquire one, because the admin app
 * (M2.7) and the super admin app wire the same components to the same endpoints through
 * their own clients. What lives here is the ceremony and the states around it.
 *
 * **Graceful when unsupported** means *absent*, not disabled: a greyed-out "Use a
 * passkey" on a browser that cannot do it is a dead end the person will keep clicking.
 * `fallback` lets the caller put something else in its place, and the default is
 * nothing at all.
 */

type SharedProps = Pick<ButtonProps, 'variant' | 'size' | 'block' | 'className'> & {
  children?: ReactNode;
  /** Shown instead of the button where WebAuthn is unavailable. Default: nothing. */
  fallback?: ReactNode;
  /** A ceremony that failed for a reason worth showing. Cancellation is not one. */
  onError?: (message: string, cause: unknown) => void;
  disabled?: boolean;
};

export type PasskeyRegisterButtonProps = SharedProps & {
  /** `GET /auth/passkey/generate-register-options`. */
  getOptions: () => Promise<PasskeyRegistrationOptionsJson>;
  /** `POST /auth/passkey/verify-registration` with `{ response }`. */
  verify: (response: PasskeyRegistrationResponseJson) => Promise<void>;
  onRegistered?: () => void;
};

export function PasskeyRegisterButton({
  getOptions,
  verify,
  onRegistered,
  fallback = null,
  onError,
  children = 'Add a passkey',
  variant = 'secondary',
  size,
  block,
  className,
  disabled,
}: PasskeyRegisterButtonProps) {
  const [pending, setPending] = useState(false);

  if (!isPasskeySupported()) return <>{fallback}</>;

  const run = (): void => {
    setPending(true);
    void (async () => {
      try {
        await verify(await createPasskey(await getOptions()));
        onRegistered?.();
      } catch (caught) {
        onError?.(describePasskeyError(caught) ?? messageOf(caught), caught);
      } finally {
        setPending(false);
      }
    })();
  };

  return (
    <Button
      variant={variant}
      size={size}
      block={block}
      className={className}
      icon={<KeyRound size={16} strokeWidth={2} aria-hidden={true} />}
      loading={pending}
      loadingLabel="Waiting for your device"
      disabled={disabled}
      onClick={run}
    >
      {children}
    </Button>
  );
}

export type PasskeySignInButtonProps = SharedProps & {
  /** `GET /auth/passkey/generate-authenticate-options`. */
  getOptions: () => Promise<PasskeyAuthenticationOptionsJson>;
  /** `POST /auth/passkey/verify-authentication` with `{ response }`. */
  verify: (response: PasskeyAuthenticationResponseJson) => Promise<void>;
  /** A session (or a satisfied step-up) now exists. */
  onVerified?: () => void;
};

export function PasskeySignInButton({
  getOptions,
  verify,
  onVerified,
  fallback = null,
  onError,
  children = 'Use a passkey',
  variant = 'secondary',
  size,
  block,
  className,
  disabled,
}: PasskeySignInButtonProps) {
  const [pending, setPending] = useState(false);

  if (!isPasskeySupported()) return <>{fallback}</>;

  const run = (): void => {
    setPending(true);
    void (async () => {
      try {
        await verify(await assertPasskey(await getOptions()));
        onVerified?.();
      } catch (caught) {
        onError?.(describePasskeyError(caught) ?? messageOf(caught), caught);
      } finally {
        setPending(false);
      }
    })();
  };

  return (
    <Button
      variant={variant}
      size={size}
      block={block}
      className={className}
      icon={<KeyRound size={16} strokeWidth={2} aria-hidden={true} />}
      loading={pending}
      loadingLabel="Waiting for your device"
      disabled={disabled}
      onClick={run}
    >
      {children}
    </Button>
  );
}

function messageOf(error: unknown): string {
  if (error instanceof Error && error.message !== '') return error.message;
  return 'That did not work. Please try again.';
}
